import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { VideoAutoplayService } from '../../services/video-autoplay.service';
import { VideoThumbnailService } from '../../services/video-thumbnail.service';


@Component({
  selector: 'ark-lib-plyr-video',
  templateUrl: './plyr-video.component.html',
  styleUrls: ['./plyr-video.component.css'],
})
export class PlyrVideoComponent implements AfterViewInit, OnDestroy {
  @ViewChild('proxyIframe') proxyIframe?: ElementRef<HTMLIFrameElement>;

  private sanitizer = inject(DomSanitizer);
  private videoAutoplay = inject(VideoAutoplayService);
  private hostRef = inject(ElementRef);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private videoThumbnail = inject(VideoThumbnailService);

  videoUrl = input<string>();
  videoId = input('');

  provider = input<'youtube' | 'vimeo' | 'hls'>('youtube');
  options = input<Record<string, any>>({});
  autoplayOnViewport = input(false);
  showThumbnail = input(true);
  fallbackThumbnail = input<string>();
  preloadOnNearViewport = input(false);
  preloadRootMargin = input('720px 0px');

  @Output() playerReady = new EventEmitter<any>();

  iframeLoaded = signal(false);
  videoDisplayReady = signal(false);
  private playerLoadAllowed = signal(false);
  private vimeoThumbnailUrl = signal<string | null>(null);

  private readonly minimumThumbnailDurationMs = 750;
  private thumbnailShownAt = Date.now();
  private revealTimer: ReturnType<typeof setTimeout> | null = null;
  private preloadObserver: IntersectionObserver | null = null;
  private thumbnailSubscription: Subscription | null = null;
  private navigationSubscription: Subscription;
  private viewportShouldPlay = false;

  private videoInfo = computed(() => {
    const url = this.videoUrl();
    return url ? this.extractVideoInfo(url) : null;
  });

  private resolvedProvider = computed(
    () => this.videoInfo()?.platform ?? this.provider()
  );

  private resolvedVideoId = computed(() => {
    const info = this.videoInfo();

    if (info) return info.id;
    return this.videoId();
  });

  thumbnailUrl = computed(() => {
    const id = this.resolvedVideoId();
    const provider = this.resolvedProvider();

    if (id && provider === 'youtube') {
      return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }

    if (provider === 'vimeo' && this.vimeoThumbnailUrl()) {
      return this.vimeoThumbnailUrl();
    }

    return this.fallbackThumbnail() ?? null;
  });

  proxyUrl = computed<SafeResourceUrl | null>(() => {
    if (!this.playerLoadAllowed()) return null;

    const id = this.resolvedVideoId();
    if (!id) return null;

    let proxyFile = 'proxy/youtube.html';

    if (this.resolvedProvider() === 'vimeo') {
      proxyFile = 'proxy/vimeo.html';
    }

    if (this.resolvedProvider() === 'hls') {
      proxyFile = 'proxy/hls.html';
    }

    const params = new URLSearchParams({
      v: id,
      controls: '1',
    });

    if (this.autoplayOnViewport()) {
      params.set('mute', '1');
    }

    Object.entries(this.options()).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });

    const url = new URL(proxyFile, this.document.baseURI);
    url.search = params.toString();

    return this.sanitizer.bypassSecurityTrustResourceUrl(url.toString());
  });

  constructor() {
    this.navigationSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
      )
      .subscribe(() => this.pauseForLifecycle());

    effect(
      () => {
        this.proxyUrl();
        this.clearRevealTimer();
        this.thumbnailShownAt = Date.now();
        this.iframeLoaded.set(false);
        this.videoDisplayReady.set(false);
        this.playerReady.emit(null);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const info = this.videoInfo();
        const sourceUrl = this.videoUrl();
        const canLoad = this.playerLoadAllowed();

        this.thumbnailSubscription?.unsubscribe();
        this.thumbnailSubscription = null;
        this.vimeoThumbnailUrl.set(null);

        if (
          info?.platform !== 'vimeo' ||
          !sourceUrl ||
          !canLoad ||
          !this.showThumbnail()
        ) {
          return;
        }

        this.thumbnailSubscription = this.videoThumbnail
          .getVimeoThumbnail(sourceUrl)
          .subscribe((thumbnailUrl) => {
            this.vimeoThumbnailUrl.set(thumbnailUrl);
          });
      },
      { allowSignalWrites: true },
    );
  }

  ngAfterViewInit(): void {
    this.setupNearViewportPreload();
    this.setupViewportAutoplay();
  }

  ngOnDestroy(): void {
    this.pauseForLifecycle();
    this.clearRevealTimer();
    this.thumbnailSubscription?.unsubscribe();
    this.navigationSubscription.unsubscribe();
    this.preloadObserver?.disconnect();
    this.teardownViewportAutoplay();
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (this.document.hidden) {
      this.pauseForLifecycle();
    }
  }

  onIframeLoad(): void {
    this.iframeLoaded.set(true);
    this.playerReady.emit(true);

    if (this.autoplayOnViewport() && this.viewportShouldPlay) {
      this.play();
    }
  }

  @HostListener('window:message', ['$event'])
  onProxyMessage(event: MessageEvent): void {
    if (event.source !== this.proxyIframe?.nativeElement.contentWindow) return;

    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }

    if (data?.event === 'player-ready') {
      if (this.autoplayOnViewport() && this.viewportShouldPlay) {
        this.play();
      }
      return;
    }

    if (data?.event !== 'video-display-ready') return;


    const elapsed = Date.now() - this.thumbnailShownAt;
    const delay = Math.max(0, this.minimumThumbnailDurationMs - elapsed);

    this.clearRevealTimer();
    this.revealTimer = setTimeout(() => {
      this.videoDisplayReady.set(true);
      this.revealTimer = null;
    }, delay);
  }

  play(): void {
    this.sendCommand('playVideo');
  }

  pause(): void {
    this.sendCommand('pauseVideo');
  }

  private pauseForLifecycle(): void {
    this.viewportShouldPlay = false;
    this.pause();
  }

  private sendCommand(func: string): void {
    const iframeWindow = this.proxyIframe?.nativeElement?.contentWindow;
    iframeWindow?.postMessage(
      JSON.stringify({
        event: 'command',
        func,
      }),
      '*'
    );
  }

  private extractVideoInfo(
    url: string
  ): { platform: 'youtube' | 'vimeo' | 'hls'; id: string } | null {
    const normalizedUrl = url.trim();
    if (!normalizedUrl) return null;

    if (/\.m3u8(?:[?#].*)?$/i.test(normalizedUrl)) {
      return { platform: 'hls', id: normalizedUrl };
    }

    const youtubeMatch = normalizedUrl.match(
      /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^a-zA-Z0-9_-]|$)/
    );
    if (youtubeMatch?.[1]) {
      return { platform: 'youtube', id: youtubeMatch[1] };
    }

    const vimeoMatch = normalizedUrl.match(
      /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/
    );
    if (vimeoMatch?.[1]) {
      return { platform: 'vimeo', id: vimeoMatch[1] };
    }

    return null;
  }

  private setupViewportAutoplay(): void {
    if (!this.autoplayOnViewport()) return;

    this.videoAutoplay.observe(
      this.hostRef.nativeElement,
      (shouldPlay: boolean) => {
        this.viewportShouldPlay = shouldPlay;
        if (shouldPlay) {
          this.play();
        } else {
          this.pause();
        }
      }
    );
  }

  private setupNearViewportPreload(): void {
    if (!this.preloadOnNearViewport()) {
      this.playerLoadAllowed.set(true);
      return;
    }

    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.playerLoadAllowed.set(true);
        this.preloadObserver?.disconnect();
        this.preloadObserver = null;
      },
      {
        root: null,
        rootMargin: this.preloadRootMargin(),
        threshold: 0,
      },
    );

    this.preloadObserver.observe(this.hostRef.nativeElement);
  }

  private teardownViewportAutoplay(): void {
    if (this.autoplayOnViewport()) {
      this.videoAutoplay.unobserve(this.hostRef.nativeElement);
    }
  }

  private clearRevealTimer(): void {
    if (this.revealTimer === null) return;
    clearTimeout(this.revealTimer);
    this.revealTimer = null;
  }
}
